import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	useReactTable,
} from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { GlobalState } from "context/GlobalContext";

import axios from "axios";

import UploadFileIcon from "@mui/icons-material/UploadFile";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import EditModal from "./EditModal/EditModal";
import { inputTypes } from "data/pimData";
import FetchNewStoresDialog from "./FetchNewStoresDialog";
import StoreMasterUploadDialog from "./StoreMasterUploadDialog";

const inputColumns = {
	ginesys_store_name: {
		name: "Ginesys Store Name",
		key: "full_name",
		group: "EBO_STORE_POS",
		type: { notNull: true, datatype: "string", unique: true },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	short_name: {
		name: "Store Short Name",
		key: "short_name",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	store_code: {
		name: "Store Code",
		key: "site_code",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	fofo_code: {
		name: "Fofo Code",
		key: "fofo_code",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.autoGenerate,
		display: true,
	},
	upi_id_1: {
		name: "UPI Id 1",
		key: "upi_id_1",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	upi_id_2: {
		name: "UPI Id 2",
		key: "upi_id_2",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	hdfc_card_tid_1: {
		name: "HDFC Card TID 1",
		key: "card_tid_1",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	hdfc_card_tid_2: {
		name: "HDFC Card TID 2",
		key: "card_tid_2",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	amex_id: {
		name: "Amex Id",
		key: "amex_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	writer_cash_pickup: {
		name: "Writer Cash Pickup",
		key: "writer_cash_pickup",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	phonepe_id: {
		name: "Phonepe Id",
		key: "phonepe_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	pay_by_link: {
		name: "Pay By Link",
		key: "pay_by_link",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	magicpin_id: {
		name: "Magicpin ID",
		key: "magicpin_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	zomato_id: {
		name: "Zomato ID",
		key: "zomato_id",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
	status: {
		name: "Status",
		key: "Status",
		group: "EBO_STORE_POS",
		type: { notNull: false, datatype: "string", unique: false },
		input_type: inputTypes.enter,
		display: true,
	},
};

const tableColumns = Object.keys(inputColumns).map((col) => ({
	accessorKey: inputColumns[col].key,
	header: inputColumns[col].name,
	cell: (info) => {
		const value = info.getValue();
		return value === null || value === undefined || value === "" ? "-" : String(value);
	},
}));

const toCsvValue = (value) => {
	const str = value === null || value === undefined ? "" : String(value);
	return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const exportRowsToCsv = (rows, fileName) => {
	const header = tableColumns.map((column) => toCsvValue(column.header)).join(",");
	const body = rows
		.map((row) => tableColumns.map((column) => toCsvValue(row.getValue(column.accessorKey))).join(","))
		.join("\n");
	const csv = `${header}\n${body}`;
	const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = `${fileName}.csv`;
	link.click();
	URL.revokeObjectURL(url);
};

const StoreTidMaster = () => {
	const [allStores, setAllStores] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedStore, setSelectedStore] = useState();

	// def value is true becoz for textfield disabled =true;
	const [modalOpen, setModalOpen] = useState(false);
	const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const [sorting, setSorting] = useState([]);
	const [globalFilter, setGlobalFilter] = useState("");
	const { setSnackBar, setSnackBarOpen } = GlobalState();

	const columns = useMemo(() => tableColumns, []);

	const table = useReactTable({
		data: allStores,
		columns,
		state: { sorting, globalFilter },
		onSortingChange: setSorting,
		onGlobalFilterChange: setGlobalFilter,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		initialState: { pagination: { pageSize: 25 } },
	});

	// console.log(selectedStore, allStores);
	const submitFunction = () => {};
	// fetch costings on load
	const fetchStores = async () => {
		try {
			setLoading(true);
			const { data } = await axios.get("/api/pos_reco/get_store_master_bq");
			const stores = Array.isArray(data) ? data : data?.result || [];
			setAllStores(
				stores.map((store, index) => ({
					...store,
					id: store?.id ?? store?.site_code ?? store?.short_name ?? `store-${index}`,
				})),
			);
			setLoading(false);
		} catch (error) {
			setLoading(false);
			setSnackBarOpen(true);
			setSnackBar({
				message: "Network Error",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		}
	};

	useEffect(() => {
		fetchStores();
	}, []);

	const handleGridRowClick = (data) => {
		setModalOpen(true);
		setSelectedStore(data);
	};
	return (
		<Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
			<Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, pr: 1 }}>
				<Box display="flex" gap={1}>
					<Button
						size="small"
						variant="contained"
						startIcon={<UploadFileIcon />}
						onClick={() => setUploadDialogOpen(true)}>
						Upload
					</Button>
					<Button
						size="small"
						variant="outlined"
						startIcon={<SyncRoundedIcon />}
						onClick={() => setFetchDialogOpen(true)}>
						Fetch New Stores
					</Button>
				</Box>
			</Box>
			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					width: "100%",
					display: "flex",
					flexDirection: "column",
					overflow: "hidden",
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 2,
					bgcolor: "background.paper",
				}}>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						gap: 1,
						padding: "10px 12px 6px",
						borderBottom: "1px solid",
						borderColor: "divider",
					}}>
					<TextField
						size="small"
						variant="outlined"
						placeholder="Search..."
						value={globalFilter ?? ""}
						onChange={(event) => setGlobalFilter(event.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchRoundedIcon fontSize="small" />
								</InputAdornment>
							),
							sx: { borderRadius: 1.5, backgroundColor: "grey.50" },
						}}
					/>
					<Button
						size="small"
						startIcon={<FileDownloadOutlinedIcon />}
						onClick={() =>
							exportRowsToCsv(table.getFilteredRowModel().rows, "pos_store_master")
						}>
						Export
					</Button>
				</Box>
				{loading && <LinearProgress />}
				<TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
					<Table stickyHeader size="small">
						<TableHead>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => {
										const sortDirection = header.column.getIsSorted();
										return (
											<TableCell
												key={header.id}
												align="center"
												sortDirection={sortDirection || false}
												sx={{
													backgroundColor: "grey.50",
													fontWeight: 500,
													color: "text.secondary",
													textTransform: "uppercase",
													fontSize: "0.7rem",
													letterSpacing: "0.4px",
													whiteSpace: "nowrap",
												}}>
												<TableSortLabel
													active={!!sortDirection}
													direction={sortDirection || "asc"}
													onClick={header.column.getToggleSortingHandler()}>
													{flexRender(header.column.columnDef.header, header.getContext())}
												</TableSortLabel>
											</TableCell>
										);
									})}
								</TableRow>
							))}
						</TableHead>
						<TableBody>
							{table.getRowModel().rows.length === 0 ? (
								<TableRow>
									<TableCell colSpan={columns.length} align="center">
										<Typography variant="body2" sx={{ my: 2 }}>
											{loading ? "Loading..." : "No stores found"}
										</Typography>
									</TableCell>
								</TableRow>
							) : (
								table.getRowModel().rows.map((row) => (
									<TableRow
										key={row.id}
										hover
										onClick={() => handleGridRowClick(row.original)}
										sx={{ cursor: "pointer" }}>
										{row.getVisibleCells().map((cell) => (
											<TableCell
												key={cell.id}
												align="center"
												sx={{ fontSize: "0.82rem", whiteSpace: "nowrap" }}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={table.getFilteredRowModel().rows.length}
					page={table.getState().pagination.pageIndex}
					onPageChange={(event, newPage) => table.setPageIndex(newPage)}
					rowsPerPage={table.getState().pagination.pageSize}
					onRowsPerPageChange={(event) => table.setPageSize(Number(event.target.value))}
					rowsPerPageOptions={[10, 25, 50]}
					sx={{ borderTop: "1px solid", borderColor: "divider", flexShrink: 0 }}
				/>
			</Box>
			{/* edit Store Data */}
			<EditModal
				columns={inputColumns}
				allStores={allStores}
				setAllStores={setAllStores}
				selectedStore={selectedStore}
				setSelectedStore={setSelectedStore}
				modalOpen={modalOpen}
				setModalOpen={setModalOpen}
				submitFunction={submitFunction}
				onUpdateSuccess={fetchStores}
			/>
			<FetchNewStoresDialog open={fetchDialogOpen} onClose={() => setFetchDialogOpen(false)} />
			<StoreMasterUploadDialog
				open={uploadDialogOpen}
				onClose={() => setUploadDialogOpen(false)}
				onUploadSuccess={fetchStores}
			/>
		</Box>
	);
};

export default StoreTidMaster;
